import { useEffect, useRef, useState } from "react";
import { LuColumns } from "react-icons/lu";
import { btnGhost } from "./styles";

export interface ColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean;
}

// Remembers which table columns are shown, per table, across reloads.
export const useColumnVisibility = (
  storageKey: string,
  columns: ColumnDef[]
) => {
  const [visible, setVisible] = useState<Record<string, boolean>>(() => {
    const defaults: Record<string, boolean> = {};
    columns.forEach((c) => (defaults[c.key] = c.defaultVisible !== false));
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) ?? "{}");
      return { ...defaults, ...saved };
    } catch {
      return defaults;
    }
  });

  const toggle = (key: string) =>
    setVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });

  const isVisible = (key: string) => visible[key] !== false;
  const visibleCount = columns.filter((c) => isVisible(c.key)).length;

  return { isVisible, toggle, visibleCount };
};

const ColumnMenu = ({
  columns,
  isVisible,
  onToggle,
}: {
  columns: ColumnDef[];
  isVisible: (key: string) => boolean;
  onToggle: (key: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${btnGhost} flex items-center gap-2`}
      >
        <LuColumns /> Columns
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 bg-white border border-hcBorder rounded-xl shadow-pop z-20 p-2 max-h-80 overflow-auto">
          {columns.map((c) => (
            <label
              key={c.key}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-honeConnectBlack hover:bg-hcBg cursor-pointer"
            >
              <input
                type="checkbox"
                checked={isVisible(c.key)}
                onChange={() => onToggle(c.key)}
                className="accent-honeConnectPurple"
              />
              {c.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default ColumnMenu;
